import React, { useContext, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import LinkedModelsTable from './LinkedModelsTable';
import { FieldMappingDrawer } from '../InputParameter';
import { TLinkedModel, TUpsertISRProjectDataModelsInput } from "../../Pages/DefineModel/model";
import { getISRProjects } from '../../Pages/DefineModel/ModelServices/ModalServices';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { AppContext } from 'Context/AppContext';
import { useISRContext } from '../../Hooks/useISRContext';

// Simple unique ID generator
const generateId = (): string => {
    return `linked_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// Temporary restriction: only one linked model is supported for now
const MAX_LINKED_MODELS = 1;

type LinkedModelsConfigProps = {
    id: string;
    projectId: string;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
}

type ModelOption = {
    value: string;
    label: string;
};

const LinkedModelsConfig = ({
                                 id,
                                 projectId,
                                 userProjectDataModels,
                                 setUserProjectDataModels
                             }: LinkedModelsConfigProps) => {
    const appContext = useContext(AppContext);
    const isrContext = useISRContext();
    const location = useLocation();
    const isGuideDetail = Boolean(location.pathname.includes('/guide/'));

    const [linkedModels, setLinkedModels] = useState<TLinkedModel[]>(userProjectDataModels.project.linkedModels ?? []);
    const [modelOptions, setModelOptions] = useState<ModelOption[]>([]);
    const [modelsLoading, setModelsLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingLinkedModelId, setEditingLinkedModelId] = useState<string | null>(null);

    // Load all models available in the current channel-environment (excluding this model itself)
    useEffect(() => {
        const channelKey = appContext?.channelKey;
        const industryId = isrContext?.industryId;
        if (!channelKey || !industryId) return;

        let isMounted = true;
        setModelsLoading(true);
        getISRProjects(isGuideDetail, industryId, channelKey, { pageNumber: 1, pageSize: 10000 }, 'ISRName', 'asc', '')
            .then((response) => {
                if (!isMounted) return;
                const projects = response?.done ? (response.data ?? []) : [];
                setModelOptions(
                    projects
                        .filter((project: any) => project.id !== projectId)
                        .map((project: any) => ({ value: project.id, label: project.ISRName }))
                );
            })
            .finally(() => {
                if (isMounted) setModelsLoading(false);
            });

        return () => { isMounted = false; };
    }, [appContext?.channelKey, isrContext?.industryId, isGuideDetail, projectId]);

    const saveLinkedModels = (linkedModels: TLinkedModel[]) => {
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            return {
                ...prevDataModels,
                project: {
                    ...prevDataModels.project,
                    linkedModels,
                },
            };
        });
    }

    const handleAddLinkedModel = useCallback(() => {
        const newLinkedModel: TLinkedModel = {
            rowId: generateId(),
            id: '',
            description: '',
            createdAt: new Date().toISOString(),
        };
        setLinkedModels(prev => {
            if (prev.length >= MAX_LINKED_MODELS) return prev;
            const updated = [...prev, newLinkedModel];
            saveLinkedModels(updated);
            return updated;
        });
    }, []);

    const handleDeleteLinkedModel = useCallback((rowId: string) => {
        setLinkedModels(prev => {
            const updated = prev.filter(lm => lm.rowId !== rowId);
            saveLinkedModels(updated);
            return updated;
        });
    }, []);

    const handleUpdateLinkedModel = useCallback((rowId: string, updates: Partial<TLinkedModel>) => {
        setLinkedModels(prev => {
            const updated = prev.map(lm =>
                lm.rowId === rowId ? { ...lm, ...updates } : lm
            );
            saveLinkedModels(updated);
            return updated;
        });
    }, []);

    const handleOpenFieldSelector = useCallback((rowId: string) => {
        setEditingLinkedModelId(rowId);
        setIsDrawerOpen(true);
    }, []);

    const handleFieldSelected = useCallback((fieldId: string, subModelId: string | undefined, _displayName: string) => {
        if (editingLinkedModelId) {
            handleUpdateLinkedModel(editingLinkedModelId, {
                condition: {
                    subModelId: subModelId ?? '',
                    fieldId: fieldId
                }
            });
        }
        setIsDrawerOpen(false);
        setEditingLinkedModelId(null);
    }, [editingLinkedModelId, handleUpdateLinkedModel]);

    const handleCloseDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setEditingLinkedModelId(null);
    }, []);

    return (
        <div id={id} className="tw-h-full tw-pt-4 tw-ps-4 tw-bg-grey-100">
            <div className="tw-p-6 tw-bg-white">
                {/* Header */}
                <div className="tw-mb-4">
                    <Typography variant="heading-2" className="tw-text-grey-900">
                        Linked Model
                    </Typography>
                </div>

                {/* Warning Note */}
                <div id="linked-models-warning" className="tw-max-w-4xl tw-mb-6">
                    <div className="tw-bg-tone-warning-tint tw-px-2 tw-py-1 tw-rounded-sm tw-flex tw-items-center">
                        <Typography variant="sub-body-supporting" className="tw-text-yellow-warning-900">
                            Note: The linked model is included at runtime only when the condition field (if selected) evaluates to true. Leave the condition field blank to always link the model.
                        </Typography>
                    </div>
                </div>

                {/* Linked Models Table */}
                <LinkedModelsTable
                    linkedModels={linkedModels}
                    modelOptions={modelOptions}
                    modelsLoading={modelsLoading}
                    onAddLinkedModel={handleAddLinkedModel}
                    onDeleteLinkedModel={handleDeleteLinkedModel}
                    onUpdateLinkedModel={handleUpdateLinkedModel}
                    onOpenFieldSelector={handleOpenFieldSelector}
                    userProjectDataModels={userProjectDataModels}
                />

                {/* Condition Field Selection Drawer - boolean fields only */}
                <FieldMappingDrawer
                    isOpen={isDrawerOpen}
                    onClose={handleCloseDrawer}
                    onFieldSelected={handleFieldSelected}
                    userProjectDataModels={userProjectDataModels}
                    allowedTypes={['boolean']}
                    title="Select Condition Field"
                />
            </div>
        </div>
    );
};

export default LinkedModelsConfig;
